import { FormEvent, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { to: '/', label: '首页' },
  { to: '/movies', label: '电影' },
  { to: '/tvshows', label: '电视剧' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
    setKeyword('');
  };

  const linkClass = ({ isActive }: { isActive: boolean }) => {
    const base =
      'relative px-1 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap';
    if (transparent) {
      return `${base} ${isActive ? 'text-accent-300' : 'text-background-100 hover:text-accent-300'}`;
    }
    return `${base} ${isActive ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'}`;
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-gradient-to-b from-black/60 to-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
      }`}
    >
      <nav className="w-full px-4 md:px-8">
        <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between gap-4 md:h-[72px]">
          {/* Brand */}
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                transparent ? 'bg-accent-500' : 'bg-primary-600'
              }`}
            >
              <i className="ri-movie-2-line text-xl text-background-50" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-lg font-extrabold tracking-tight md:text-xl ${
                  transparent ? 'text-background-50' : 'text-primary-700'
                }`}
              >
                星辰影院
              </span>
              <span
                className={`mt-0.5 hidden text-[10px] tracking-[0.25em] sm:block ${
                  transparent ? 'text-background-200' : 'text-secondary-500'
                }`}
              >
                STAR CINEMA
              </span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden items-center gap-7 md:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === '/'}>
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* Desktop search */}
          <form onSubmit={submitSearch} className="hidden md:block">
            <div
              className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 transition-colors ${
                transparent
                  ? 'border-background-50/40 bg-black/20'
                  : 'border-background-300 bg-background-100'
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center ${
                  transparent ? 'text-background-100' : 'text-foreground-500'
                }`}
              >
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、剧集"
                className={`w-32 bg-transparent text-sm outline-none lg:w-44 ${
                  transparent
                    ? 'text-background-50 placeholder:text-background-200'
                    : 'text-foreground-800 placeholder:text-foreground-400'
                }`}
              />
            </div>
          </form>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <Link
              to="/search"
              aria-label="搜索"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
                transparent ? 'text-background-50' : 'text-foreground-700'
              }`}
            >
              <i className="ri-search-line text-xl" />
            </Link>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
                transparent ? 'text-background-50' : 'text-foreground-700'
              }`}
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile panel */}
      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-4">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、剧集"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <div className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}